import React, { useState } from 'react';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const SESVerification: React.FC = () => {
  const [isChecking, setIsChecking] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<{
    domain: 'pending' | 'verified' | 'failed';
    email: 'pending' | 'verified' | 'failed';
    error?: string;
  }>({
    domain: 'pending',
    email: 'pending'
  });

  const checkSESStatus = async () => {
    setIsChecking(true);
    try {
      // This would normally check AWS SES API
      // For now, we'll simulate the check
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Simulate verification status
      setVerificationStatus({
        domain: 'verified',
        email: 'verified'
      });
    } catch (error) {
      setVerificationStatus({
        domain: 'failed',
        email: 'failed',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsChecking(false);
    }
  };

  const getStatusIcon = (status: 'pending' | 'verified' | 'failed') => {
    switch (status) {
      case 'verified':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusText = (status: 'pending' | 'verified' | 'failed') => {
    switch (status) {
      case 'verified':
        return 'Verified';
      case 'failed':
        return 'Failed';
      default:
        return 'Pending';
    }
  };

  const getStatusColor = (status: 'pending' | 'verified' | 'failed') => {
    switch (status) {
      case 'verified':
        return 'text-green-600 bg-green-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-yellow-600 bg-yellow-100';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-gray-900">AWS SES Verification</h3>
        <button
          onClick={checkSESStatus}
          disabled={isChecking}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isChecking ? 'Checking...' : 'Check SES Status'}
        </button>
      </div>

      <div className="space-y-4">
        {/* Domain Verification */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center space-x-3">
            {getStatusIcon(verificationStatus.domain)}
            <div>
              <h4 className="font-medium text-gray-900">Domain Verification</h4>
              <p className="text-sm text-gray-600">scaledots.com</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(verificationStatus.domain)}`}>
            {getStatusText(verificationStatus.domain)}
          </span>
        </div>

        {/* Email Verification */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center space-x-3">
            {getStatusIcon(verificationStatus.email)}
            <div>
              <h4 className="font-medium text-gray-900">Email Verification</h4>
              <p className="text-sm text-gray-600">info@scubamail.io</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(verificationStatus.email)}`}>
            {getStatusText(verificationStatus.email)}
          </span>
        </div>

        {verificationStatus.error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{verificationStatus.error}</p>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Next Steps:</h4>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>Go to AWS SES Console</li>
            <li>Verify your domain: <code className="bg-blue-100 px-1 rounded">scaledots.com</code></li>
            <li>Verify your email: <code className="bg-blue-100 px-1 rounded">info@scubamail.io</code></li>
            <li>Request production access if needed</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default SESVerification;
